/**
 * @Description 本品牌门店
 */

import { isArray, isNotEmpty } from '@lhb/func';
import { v4 } from 'uuid';
import { useMethods } from '@lhb/hook';
import { Col, Row, Typography } from 'antd';
import { FC, useEffect, useMemo, useRef } from 'react';
import { targetStandardShop, targetLegendIcon, targetMallStandardShop } from '../../../ts-config';
import cs from 'classnames';
import styles from '../index.module.less';
import ReactDOM from 'react-dom';
import IconFont from '@/common/components/IconFont';
import { mapContainer } from '..';
import { StoreStatus } from '@/common/components/business/ExpandStore/ts-config';
import { dateFormatShow } from '@/common/utils/ways';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
const targetKeys = [
  ...targetStandardShop,
  ...targetMallStandardShop,
];
const { Text } = Typography;

/** 门店状态颜色值 */
const StoreStatusColor = {
  [StoreStatus.Signed]: '#2BC58B', // 已签约
  [StoreStatus.HouseDelivered]: '#2BC58B', // 已交房
  [StoreStatus.Opened]: '#006AFF', // 开业中
  [StoreStatus.Closed]: '#999999', // 已闭店
  [StoreStatus.Relocated]: '#999999', // 已迁址
  [StoreStatus.Terminated]: '#999999', // 已解约
};

const StandardShopCard: FC<any> = ({
  itemData,
  markerRef,
}) => {
  const detailInfos = useMemo(() => {
    const { status, typeName, contractStart, contractEnd, daysRemainingStr, openDate, closeDate } = itemData;
    if ([StoreStatus.Signed, StoreStatus.HouseDelivered].includes(status)) {
      const start = contractStart ? dateFormatShow(contractStart) : '';
      const end = contractEnd ? dateFormatShow(contractEnd) : '';

      const arr: any = [];
      typeName && arr.push({ title: '门店类型：', value: typeName, labelLength: 5 });
      daysRemainingStr && arr.push({ title: '门店保护：', value: daysRemainingStr, labelLength: 5 });
      (start || end) && arr.push({ title: '合同起始时间：', value: `${start || ''}-${end || ''}`, labelLength: 7, span: 24 });
      return arr;
    } else if (StoreStatus.Opened === status) {
      const arr: any = [];
      typeName && arr.push({ title: '门店类型：', value: typeName, labelLength: 5 });
      daysRemainingStr && arr.push({ title: '门店保护：', value: daysRemainingStr, labelLength: 5 });
      openDate && arr.push({ title: '开业时间：', value: dateFormatShow(openDate), labelLength: 5 });
      return arr;
    } else if (StoreStatus.Closed === status) {
      const arr: any = [];
      typeName && arr.push({ title: '门店类型：', value: typeName, labelLength: 5 });
      daysRemainingStr && arr.push({ title: '门店保护：', value: daysRemainingStr, labelLength: 5 });
      openDate && arr.push({ title: '开业时间：', value: dateFormatShow(openDate), labelLength: 5 });
      closeDate && arr.push({ title: '闭店时间：', value: dateFormatShow(closeDate), labelLength: 5 });
      return arr;
    } else if ([StoreStatus.Relocated, StoreStatus.Terminated].includes(status)) {
      return typeName ? [
        { title: '门店类型：', value: typeName, labelLength: 5 },
      ] : [];
    }
    return [];
  }, [itemData]);

  const methods = useMethods({
    handleClose() {
      markerRef?.setContent('<div></div>');
    }
  });
  return <div className={styles.standardshopCardCon}>
    <div className={cs('c-222 fs-14 bold', styles.cardHead)}>
      本品牌门店
      <div className={styles.close} onClick={() => methods.handleClose()}>
        <IconFont iconHref='iconic-closexhdpi' className='c-666 fs-12' />
      </div>
    </div>

    <div className={cs('mt-8', styles.nameRow)}>
      <div className={styles.picAndName}>
        <div className={styles.picWrapper}>
          <img src={itemData?.logo || `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/map/img_brand.png`}/>
        </div>
        <Typography.Text style={{ width: 180 }} className={styles.name} ellipsis={{ tooltip: itemData?.name }}>
          {itemData?.name}
        </Typography.Text>
      </div>

      <div className={styles.nameAndStatus}>
        <span className={styles.status} style={{ color: StoreStatusColor[itemData?.status] || '' }}>
          {itemData?.statusName}
        </span>
      </div>
    </div>

    <div className={cs('mt-6 mb-8', styles.chancepointAddressCon)}>
      <div>
        <IconFont iconHref='iconic_dingwei1' className='c-999 fs-14'/>
      </div>
      <Text
        style={{ width: 228 }}
        ellipsis={{ tooltip: itemData?.address }}>
        <span className='c-999 fs-12 pl-4'>
          {itemData?.address}
        </span>
      </Text>
    </div>
    {
      detailInfos.length ? <div className={styles.detailInfos}>
        <Row gutter={12}>
          {
            detailInfos.map((info, index) => (<Col key={index} span={info.span || 12} className='mt-2'>
              <V2DetailItem
                label={info.title}
                direction='horizontal'
                labelLength={info.labelLength}
                value={isNotEmpty(info.value) ? info.value : '-'}
                className={cs('fs-12 c-666', styles.item)}
              />
            </Col>))
          }
        </Row>
      </div> : <></>
    }
  </div>;
};
const StandardShop: FC<any> = ({
  mapIns,
  legendData,
  data,
  contentMarkerRef,
  onClickMarker = () => {}
}) => {
  const groupMarkersRef: any = useRef();
  // const cadrMarkerRef: any = useRef();
  const targetIconImg = useMemo(() => {
    const target = legendData?.find((item: any) => targetKeys.includes(item.key));
    return targetLegendIcon[target?.key] || '';
  }, [legendData]);

  useEffect(() => {
    if (isArray(data) && data.length) {
      methods.createMarkers();
    } else {
      groupMarkersRef.current?.forEach((item: any) => {
        mapIns.remove(item);
      });
      contentMarkerRef.current && mapIns.remove(contentMarkerRef.current);
    }
    return () => {
      groupMarkersRef.current?.forEach((item: any) => {
        mapIns && mapIns.remove(item);
      });
      contentMarkerRef.current && mapIns && mapIns.remove(contentMarkerRef.current);
    };
  }, [data]);
  const methods = useMethods({
    createMarkers: () => {
      const markers: any[] = [];
      data.forEach((item: any) => {
        const markerItem = methods.addItemMarker(item);
        markerItem && (markers.push(markerItem));
      });
      groupMarkersRef.current = markers;
    },
    addItemMarker: (item) => {
      if (!item.lng || !item.lat) return;
      const uid:any = v4();
      const { lng, lat } = item;
      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(+lng, +lat),
        anchor: 'center',
        offset: [0, -13],
      });
      mapIns.add(marker); // 先添加到地图上
      marker.setContent(`<div id="${uid}"></div>`);
      ReactDOM.render(<div
        className={styles.standardshopMarkerCon}
        onClick={() => methods.onClickMarker(item)}
      >
        <img
          src={targetIconImg}
          width='100%'
          height='100%'
        />
      </div>, document.getElementById(uid));
      return marker;
    },
    onClickMarker: (item) => {
      const dom = document.querySelector(`.${mapContainer}`);
      var pixel = mapIns.lngLatToContainer([item?.lng, item?.lat]);
      const { x, y } = pixel;
      const offset = [0, 0];
      let topBottom = 'top';
      let leftRight = 'center';
      if (y < 200) {
        topBottom = 'top';
      } else if ((dom?.clientHeight || 0) - y < 200) {
        topBottom = 'bottom';
        offset[1] = -24;
      }
      if (x < 200) {
        leftRight = 'left';
      } else if ((dom?.clientWidth || 0) - x < 200) {
        leftRight = 'right';
        offset[0] = -12;
      }
      contentMarkerRef.current && mapIns.remove(contentMarkerRef.current);
      const uid:any = v4();
      const { lng, lat } = item;
      const marker = new window.AMap.Marker({
        position: new window.AMap.LngLat(+lng, +lat),
        // anchor: 'top-center',
        anchor: `${topBottom}-${leftRight}`,
        offset,
        zIndex: 70 // 比其他覆盖物的层级高
      });
      mapIns.add(marker); // 先添加到地图上
      marker.setContent(`<div id="${uid}"></div>`);
      contentMarkerRef.current = marker;
      onClickMarker?.();
      ReactDOM.render(<StandardShopCard
        itemData={item}
        markerRef={contentMarkerRef.current}
      />, document.getElementById(uid));
    }
  });
  return (
    <></>
  );
};

export default StandardShop;
